import { Component, computed, effect, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoDirective } from '@ngneat/transloco';
import { ConfigService } from '../../core/services/config.service';
import { mergeMap, Subscription } from 'rxjs';
import { ErrorsService } from '../../core/services/errors.service';
import { OrderService } from '../../core/services/order.service';
import { OrderCustomer } from '../../core/models/ordering/order-customer.model';
import { Router } from '@angular/router';
import { InputComponent } from '../../shared/input/input.component';
import { MaskFieldInputComponent } from '../../shared/mask-field-input/mask-field-input.component';
import { IconComponent } from '../../shared/icons/icon.component';
import { SmsSignUpInformationComponent } from '../../shared/sms-sign-up-information/sms-sign-up-information.component';
import { IconNameType } from '../../shared/icons/icon-name-type.enum';
import { RoundIconType } from '../../shared/round-icon/round-icon-type.enum';
import { MaskFieldInputType } from '../../shared/mask-field-input/mask-field-input-type.enum';
import { LayoutService } from '../../core/services/layout.service';
import { FieldDisplayPolicy } from '../../core/models/core/field-display-policy.enum';
import { ToggleSwitchComponent } from '../../shared/toggle-switch/toggle-switch.component';
import { AuditService } from '../../core/services/audit.service';

@Component({
  selector: 'cwo-guest-checkout-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputComponent,
    MaskFieldInputComponent,
    ReactiveFormsModule,
    IconComponent,
    SmsSignUpInformationComponent,
    ToggleSwitchComponent,
    TranslocoDirective
],
  templateUrl: './guest-checkout-form.component.html',
  styleUrl: './guest-checkout-form.component.scss',
})
export class GuestCheckoutFormComponent {
  errorsService = inject(ErrorsService);
  orderService = inject(OrderService);
  configService = inject(ConfigService);
  configState = this.configService.configState;
  layoutService = inject(LayoutService);
  private auditService = inject(AuditService);
  isSelfOrder = computed(() => this.orderService.order().isInitialized && this.orderService.order().isSelfOrderSource);

  fieldDisplayPolicy = FieldDisplayPolicy;
  showTableNumberInput = input(false);
  showTableNumberText = input(false);
  tableNumber = input('');
  showTableNumberError = input(false);

  readonly backToSignInClicked = output<string>();
  readonly personalDetailsCompleted = output();
  readonly tableNumberChange = output<string>();
  readonly tableNumberBlur = output();

  showFirstNameError = false;
  showLastNameError = false;
  showEmailError = false;
  showMobilePhoneError = false;

  iconNameType = IconNameType;
  roundIconType = RoundIconType;
  maskFieldInputType = MaskFieldInputType;
  allErrors?: string[] = [];
  loadingSubscription: Subscription | null = null;

  private router = inject(Router);

  constructor() {
    effect(() => {
      const isSelfOrder = this.isSelfOrder();
      const storeConfig = this.configState().store;

      this.lastName.setValidators(!isSelfOrder && storeConfig?.needsLastName ? Validators.required : null);
      this.email.setValidators(!isSelfOrder && storeConfig?.guestCheckoutEmailPolicy === FieldDisplayPolicy.Require ? [Validators.required, Validators.email] : null);
      this.mobilePhone.setValidators(isSelfOrder ? null : Validators.required);

      this.lastName.updateValueAndValidity({ emitEvent: false });
      this.email.updateValueAndValidity({ emitEvent: false });
      this.mobilePhone.updateValueAndValidity({ emitEvent: false });
    });
  }

  onBackToSignInClicked() {
    this.auditService.createAudit(() => "\"Already have an account? Sign In\" button clicked.");
    this.backToSignInClicked.emit('Sign In');
  }

  guestCheckoutForm = new FormGroup({
    firstName: new FormControl('', Validators.compose([Validators.required])),
    lastName: new FormControl('', this.configState().store?.needsLastName ? Validators.compose([Validators.required]) : null),
    email: new FormControl(
      '',
      this.configState().store?.guestCheckoutEmailPolicy === FieldDisplayPolicy.Require ? Validators.compose([Validators.required, Validators.email]) : null
    ),
    mobilePhone: new FormControl('', Validators.compose([Validators.required])),
    subscribed: new FormControl(true),
    smsMarketing: new FormControl(false),
  });

  get firstName() {
    return this.guestCheckoutForm.controls.firstName;
  }

  get lastName() {
    return this.guestCheckoutForm.controls.lastName;
  }

  get email() {
    return this.guestCheckoutForm.controls.email;
  }

  get mobilePhone() {
    return this.guestCheckoutForm.controls.mobilePhone;
  }

  get mobilePhoneLength() {
    const phoneNumberLength = Number(
      this.guestCheckoutForm.value.mobilePhone?.replace(/\D/g, '').length
    );

    return phoneNumberLength === 10 || phoneNumberLength === 0;
  }

  get subscribed() {
    return this.guestCheckoutForm.controls.subscribed;
  }


  validationMessages: any = {
    name: {
      required: 'Name is required.',
    },
    firstName: {
      required: 'First Name is required.',
    },
    lastName: {
      required: 'Last Name is required.',
    },
    email: {
      required: 'Email is required.',
      email: 'Email is invalid.',
    },
    mobilePhone: {
      required: 'Phone number is required.',
      mobilePhone: 'Phone Number is invalid.',
    },
    tableNumber: {
      required: 'Table Number is required.',
    },
  };

  auditValidationMessages: object = {
    firstName: {
      required: 'Please fill in your First Name.',
    },
    lastName: {
      required: 'Please fill in your Last Name.',
    },
    email: {
      required: 'Please fill in your email.',
      email: 'Email is invalid.',
    },
    mobilePhone: {
      required: 'Please fill in your Mobile Phone number.',
      mobilePhone: 'Phone Number is invalid.',
    },
  }

  enableErrors() {
    this.showFirstNameError = true;
    if(this.isSelfOrder()) return;

    this.showLastNameError = true;
    this.showEmailError = true;
    this.showMobilePhoneError = true;
  }

  onSubmit() {
    if (!this.validateForm()) {
      this.enableErrors();
      return;
    }
    this.allErrors = [];

    this.loadingSubscription = this.updateOrderCustomer().subscribe({
      next: () => {
        this.personalDetailsCompleted.emit();
        this.loadingSubscription = null;
      },
      error: () => {
        this.loadingSubscription = null;
      }
    });
  }

  updateOrderCustomer() {
    const isSelfOrder = this.isSelfOrder();
    const customer = new OrderCustomer({
      firstName: this.guestCheckoutForm.value.firstName ? this.guestCheckoutForm.value.firstName : undefined,
      lastName: isSelfOrder ? undefined : this.guestCheckoutForm.value.lastName ?? undefined,
      email: isSelfOrder ? undefined : this.guestCheckoutForm.value.email ?? undefined,
      phone: isSelfOrder ? undefined : this.guestCheckoutForm.value.mobilePhone ?? undefined,
      subscribe: isSelfOrder ? false : this.guestCheckoutForm.value.subscribed ?? false,
      smsMarketing: isSelfOrder ? false : this.guestCheckoutForm.value.smsMarketing ?? false,
      address: this.orderService.order().customer?.address
    });
    return this.orderService
      .updateOrderCustomer(customer, false)
      .pipe(mergeMap(() => this.orderService.fetchOrder()));
  }

  validateForm() {
    return this.isSelfOrder() ? this.firstName.valid : this.guestCheckoutForm.valid;
  }
}
